import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getToken, getCurrentUser } from '../../services/keycloak/authService';
import MFASetupModal from '../../ui/MFASetupModal';
import Banner from '../../ui/Banner';
import './SecuritySettings.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen';
const ACCOUNT_CONSOLE_URL = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/account/#/security/signingin`;

const SecuritySettings = ({ embedded = false } = {}) => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(true);
  const currentUser = getCurrentUser();

  useEffect(() => {
    checkMFAStatus();
    fetchSessionInfo();
  }, []);

  const fetchSessionInfo = async () => {
    try {
      setSessionLoading(true);
      const token = getToken();

      const response = await fetch(`${API_URL}/profile/session-info`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.data) {
          const sessionInfo = data.data;
          const location = sessionInfo.location;

          // Format location string
          let locationString = 'Unknown Location';
          if (location.city === 'Local') {
            locationString = 'Local Network';
          } else if (location.city !== 'Unknown' && location.country !== 'Unknown') {
            locationString = `${location.city}, ${location.region}, ${location.country}`;
          }

          setActiveSessions([
            {
              id: 'current',
              type: 'Web Session',
              email: currentUser?.email || 'Current User',
              ip: sessionInfo.ip,
              location: locationString,
              locationDetails: location,
              lastActive: 'Just now',
              isCurrent: true,
            },
          ]);
        }
      } else {
        const errorText = await response.text();
        console.error('Session info API error:', response.status, errorText);

        // Fallback to placeholder if API fails
        setActiveSessions([
          {
            id: 'current',
            type: 'Web Session',
            email: currentUser?.email || 'Current User',
            ip: 'Unable to fetch IP',
            location: 'Unable to fetch location',
            lastActive: 'Just now',
            isCurrent: true,
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching session info:', error);
      // Fallback to placeholder
      setActiveSessions([
        {
          id: 'current',
          type: 'Web Session',
          email: currentUser?.email || 'Current User',
          ip: 'Unable to fetch IP',
          location: 'Unable to fetch location',
          lastActive: 'Just now',
          isCurrent: true,
        },
      ]);
    } finally {
      setSessionLoading(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showDisableConfirm) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [showDisableConfirm]);

  const checkMFAStatus = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/mfa/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check MFA status');
      }

      const data = await response.json();
      setMfaStatus(data);
      setLoading(false);
    } catch (err) {
      console.error('Error checking MFA status:', err);
      setLoading(false);
    }
  };

  const handleToggleMFA = (e) => {
    // Prevent any default behavior or bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Ensure we don't have duplicate modals
    if (showDisableConfirm || showSetupModal) {
      return;
    }

    if (isMfaEnabled) {
      // Show disable confirmation
      setShowDisableConfirm(true);
    } else {
      // Enable MFA - Show Setup Modal
      setShowSetupModal(true);
    }
  };

  const handleMfaSuccess = async () => {
    await checkMFAStatus();
    setNotification({
      type: 'success',
      message: 'Two-factor authentication has been enabled successfully.',
    });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleRefreshStatus = async () => {
    setLoading(true);
    await checkMFAStatus();
  };

  const handleDisableMFA = async () => {
    // Close modal first to prevent double-rendering
    setShowDisableConfirm(false);

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/mfa/disable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disable MFA');
      }

      await checkMFAStatus();
      setNotification({ type: 'success', message: 'MFA has been disabled successfully.' });
      setTimeout(() => setNotification(null), 5000);
    } catch (err) {
      console.error('Error disabling MFA:', err);
      setNotification({
        type: 'error',
        message: err.message || 'Failed to disable MFA. Please try again.',
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleCancelDisable = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowDisableConfirm(false);
  };

  const handleOverlayClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowDisableConfirm(false);
  };

  const handleModalClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (loading) {
    return <div className="security-settings-loading">Loading security settings...</div>;
  }

  const isMfaEnabled = mfaStatus?.mfaConfigured || false;
  const isMfaRequired = mfaStatus?.mfaRequired || false;

  return (
    <div className="security-settings-container">
      {!embedded && (
        <Banner
          title="Security Settings"
          subtitle="Manage your account security and authentication methods"
          type="page-header"
        />
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`notification-toast notification-${notification.type}`}>
          <div className="notification-icon">
            {notification.type === 'success' ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </div>
          <span>{notification.message}</span>
          <button className="notification-close" onClick={() => setNotification(null)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="security-settings-content">
        {/* Two-Factor Authentication Section */}
        <div className="security-section">
          <div className="section-header">
            <h2>Two-Factor Authentication</h2>
            {!isMfaEnabled && <span className="status-badge status-badge-inactive">inactive</span>}
            {isMfaEnabled && <span className="status-badge status-badge-active">active</span>}
          </div>

          <p className="section-description">
            Secure your account by enabling two-factor authentication. Choose your preferred method
            below. Only one method can be active at a time.
          </p>

          {/* MFA Toggle Control */}
          <div className="mfa-toggle-container">
            <div className="mfa-toggle-content">
              <div className="mfa-toggle-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="mfa-toggle-text">
                <div className="mfa-toggle-title">Authenticator App (TOTP)</div>
                <div className="mfa-toggle-description">
                  {isMfaEnabled
                    ? 'Two-factor authentication is currently enabled and protecting your account.'
                    : 'Enable two-factor authentication using Google Authenticator, Microsoft Authenticator, or any TOTP-compatible app.'}
                </div>
                {isMfaRequired && (
                  <div className="mfa-required-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Required for your role
                  </div>
                )}
              </div>
            </div>
            <div className="mfa-toggle-switch-wrapper">
              <label className="mfa-toggle-switch">
                <input
                  type="checkbox"
                  checked={isMfaEnabled}
                  onClick={handleToggleMFA}
                  readOnly
                  disabled={loading}
                />
                <span className="mfa-toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Active Sessions Section */}
        <div className="security-section">
          <div className="section-header">
            <h2>Active Sessions</h2>
            <span className="session-count">
              {activeSessions.length} active session{activeSessions.length !== 1 ? 's' : ''}
            </span>
          </div>

          <Banner
            description="Manage all devices that have access to your account. Remove any unfamiliar sessions immediately."
            type="info"
          />

          {sessionLoading ? (
            <div className="sessions-list">
              <div className="session-item">
                <div className="session-details">
                  <div className="session-info">Loading session information...</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="sessions-list">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className={`session-item ${session.isCurrent ? 'session-current' : ''}`}
                >
                  <div className="session-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </div>
                  <div className="session-details">
                    <div className="session-type">{session.type}</div>
                    <div className="session-info">
                      {session.email && <span>{session.email}</span>}
                      {session.ip && (
                        <span>
                          {' '}
                          • <strong>IP:</strong> {session.ip}
                        </span>
                      )}
                      {session.location && (
                        <span>
                          {' '}
                          • <strong>Location:</strong> {session.location}
                        </span>
                      )}
                    </div>
                    {session.locationDetails && session.locationDetails.timezone && (
                      <div
                        className="session-info"
                        style={{ marginTop: '4px', fontSize: '13px', color: '#64748b' }}
                      >
                        {session.locationDetails.timezone !== 'Unknown' && (
                          <span>
                            <strong>Timezone:</strong> {session.locationDetails.timezone}
                          </span>
                        )}
                        {session.locationDetails.isp &&
                          session.locationDetails.isp !== 'Unknown' && (
                            <span>
                              {' '}
                              • <strong>ISP:</strong> {session.locationDetails.isp}
                            </span>
                          )}
                      </div>
                    )}
                    <div className="session-meta">
                      <span className="session-last-active">Last active: {session.lastActive}</span>
                      {session.isCurrent && (
                        <span className="session-current-badge">Current Session</span>
                      )}
                    </div>
                  </div>
                  {!session.isCurrent && (
                    <button className="btn-remove-session" title="Remove session">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Disable MFA Confirmation Modal - Rendered via Portal */}
      {showDisableConfirm &&
        ReactDOM.createPortal(
          <div className="mfa-confirm-overlay" onClick={handleOverlayClick}>
            <div className="mfa-confirm-modal" onClick={handleModalClick}>
              <div className="mfa-confirm-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h2>Disable Two-Factor Authentication?</h2>
              <p>
                Are you sure you want to disable two-factor authentication? This will reduce your
                account security and make it easier for unauthorized users to access your account.
              </p>
              <div className="mfa-confirm-actions">
                <button className="btn-confirm-cancel" onClick={handleCancelDisable} type="button">
                  Cancel
                </button>
                <button className="btn-confirm-disable" onClick={handleDisableMFA} type="button">
                  Disable MFA
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* MFA Setup Modal */}
      <MFASetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSuccess={handleMfaSuccess}
        initialType="totp"
      />
    </div>
  );
};

export default SecuritySettings;
